import { normalizePhone } from './firebase';

const EPSILON = 0.0001;
const MIN_AUTO_SHARE_FRACTION = 0.01;
const EQUAL_SHARE_TOLERANCE = 0.001;

export const roundClaimShareFraction = (value: number) => Number(Math.max(0, Number(value || 0)).toFixed(4));

const toLockedPhoneSet = (lockedSharePhones?: Iterable<string>) => {
  const set = new Set<string>();
  if (!lockedSharePhones) return set;
  for (const phone of lockedSharePhones) {
    const normalized = normalizePhone(String(phone || ''));
    if (normalized) set.add(normalized);
  }
  return set;
};

const normalizeShareMap = (shares: Record<string, any> | undefined | null) => {
  const next: Record<string, any> = {};
  Object.entries(shares || {}).forEach(([key, value]) => {
    const fraction = Math.max(0, Number(value?.fraction || 0));
    next[key] = {
      ...(value || {}),
      fraction: Number(fraction.toFixed(4)),
    };
  });
  return next;
};

const rebalanceEqualShares = (
  shares: Record<string, any> | undefined | null,
  options?: {
    viewerKey?: string;
    lockedPhones?: Set<string>;
    organizerPhone?: string;
  },
) => {
  const normalized = normalizeShareMap(shares);
  const viewerKey = options?.viewerKey || '';
  const fixedEntries = Object.entries(normalized).filter(([key, share]) => {
    if (key === viewerKey) return share?.mode === 'custom';
    return isFixedShareForViewer(key, share, viewerKey, options?.lockedPhones, options?.organizerPhone);
  });
  const equalEntries = Object.entries(normalized).filter(([key]) => (
    !fixedEntries.some(([fixedKey]) => fixedKey === key)
  ));
  const fixedTotal = fixedEntries.reduce((sum, [_, share]) => sum + Number(share?.fraction || 0), 0);
  const remaining = Math.max(0, 1 - fixedTotal);
  if (equalEntries.length === 0) {
    return Object.fromEntries(fixedEntries);
  }

  const rebalanced = Object.fromEntries(fixedEntries);
  let allocated = 0;
  equalEntries.forEach(([key, share], index) => {
    const fraction = index === equalEntries.length - 1
      ? Math.max(0, remaining - allocated)
      : Number((remaining / equalEntries.length).toFixed(4));
    allocated += fraction;
    rebalanced[key] = {
      ...(share || {}),
      fraction: Math.max(0, Number(fraction.toFixed(4))),
      mode: 'equal',
    };
  });
  return rebalanced;
};

const isFixedShareForViewer = (
  key: string,
  share: any,
  viewerKey: string,
  lockedPhones?: Set<string>,
  organizerPhone?: string,
) => {
  if (key === viewerKey) return false;
  if (share?.isLocked || share?.mode === 'locked' || share?.paid === true || share?.mode === 'custom') return true;
  const normalized = normalizePhone(key);
  return !!normalized && Boolean(lockedPhones?.has(normalized));
};

export const computeViewerEditableShareState = (input: {
  shares: Record<string, any> | undefined | null;
  isOrganizer: boolean;
  viewerPhone: string;
  organizerPhone?: string;
  lockedSharePhones?: Iterable<string>;
}) => {
  const shares = normalizeShareMap(input.shares || {});
  const normalizedViewer = normalizePhone(input.viewerPhone || '');
  const normalizedOrganizer = normalizePhone(input.organizerPhone || '');
  const effectiveOrganizerPhone = normalizedOrganizer || normalizedViewer;
  const existingViewerKey = input.isOrganizer
    ? (shares.organizer ? 'organizer' : (resolvePhoneKey(shares, effectiveOrganizerPhone) || null))
    : (resolvePhoneKey(shares, normalizedViewer) || null);
  const viewerKey = existingViewerKey || (input.isOrganizer ? (normalizedOrganizer ? 'organizer' : normalizedViewer) : normalizedViewer);
  const lockedPhones = toLockedPhoneSet(input.lockedSharePhones);
  const totalAssigned = sumShareFractions(shares);
  const viewerShare = resolveSessionViewerShare(input);
  const viewerCurrentFraction = Math.max(0, Number((viewerShare as any)?.fraction || 0));
  const viewerPersistedMode = viewerShare?.mode === 'custom' ? 'custom' : 'equal';
  const equalLikeOtherKeys = new Set<string>();
  if (lockedPhones.size > 0 && viewerCurrentFraction > EPSILON) {
    Object.entries(shares).forEach(([key, share]) => {
      if (key === viewerKey) return;
      const fraction = Number((share as any)?.fraction || 0);
      if (fraction <= EPSILON) return;
      const normalizedKey = normalizePhone(key);
      const isOrganizerKey = key === 'organizer' || (!!effectiveOrganizerPhone && normalizedKey === effectiveOrganizerPhone);
      if (!isOrganizerKey && normalizedKey && lockedPhones.has(normalizedKey)) return;
      if (share?.mode === 'custom') return;
      if (Math.abs(fraction - viewerCurrentFraction) <= EQUAL_SHARE_TOLERANCE) {
        equalLikeOtherKeys.add(key);
      }
    });
  }
  const viewerMode = viewerPersistedMode === 'custom' && equalLikeOtherKeys.size === 0 ? 'custom' : 'equal';
  const fixedOtherSharesSum = Object.entries(shares)
    .filter(([key, share]) => (
      !equalLikeOtherKeys.has(key)
      && isFixedShareForViewer(key, share, viewerKey, lockedPhones, effectiveOrganizerPhone)
    ))
    .reduce((sum, [_, share]) => sum + Number((share as any)?.fraction || 0), 0);
  const equalizableOtherCount = Object.entries(shares)
    .filter(([key, share]) => (
      key !== viewerKey
      && (
        equalLikeOtherKeys.has(key)
        || !isFixedShareForViewer(key, share, viewerKey, lockedPhones, effectiveOrganizerPhone)
      )
      && Number((share as any)?.fraction || 0) > EPSILON
    ))
    .length;
  const unclaimedFraction = Math.max(0, 1 - totalAssigned);
  const editableMaxFraction = Math.max(0, 1 - fixedOtherSharesSum);
  const directEditableMaxFraction = Math.max(
    0,
    editableMaxFraction - (equalizableOtherCount * MIN_AUTO_SHARE_FRACTION)
  );

  return {
    viewerKey,
    totalAssigned,
    unclaimedFraction,
    editableMaxFraction,
    directEditableMaxFraction,
    equalizableOtherCount,
    hasEqualizableOthers: equalizableOtherCount > 0,
    viewerCurrentFraction,
    viewerMode,
  };
};

export const sumShareFractions = (shares: Record<string, any> | undefined | null) =>
  Object.values(shares || {}).reduce((sum: number, share: any) => sum + Number(share?.fraction || 0), 0);

export const computeUnclaimedFraction = (shares: Record<string, any> | undefined | null) =>
  Math.max(0, 1 - sumShareFractions(shares));

export const resolvePhoneKey = (
  shares: Record<string, any> | undefined | null,
  normalizedPhone: string,
) => {
  if (!shares || !normalizedPhone) return null;
  return Object.keys(shares).find((candidate) => normalizePhone(String(candidate || '')) === normalizedPhone) || null;
};

export const resolvePhoneShare = (
  shares: Record<string, any> | undefined | null,
  normalizedPhone: string,
) => {
  if (!shares || !normalizedPhone) return null;
  const key = resolvePhoneKey(shares, normalizedPhone);
  return key ? shares[key] : null;
};

export const resolveSessionViewerShare = (input: {
  shares: Record<string, any> | undefined | null;
  isOrganizer: boolean;
  viewerPhone: string;
  organizerPhone?: string;
}) => {
  const shares = input.shares || {};
  if (input.isOrganizer) {
    if (shares.organizer) return shares.organizer;
    const normalizedOrganizer = normalizePhone(input.organizerPhone || '');
    if (normalizedOrganizer) {
      const share = resolvePhoneShare(shares, normalizedOrganizer);
      if (share) return share;
    }
    const normalizedViewer = normalizePhone(input.viewerPhone || '');
    return normalizedViewer ? resolvePhoneShare(shares, normalizedViewer) : null;
  }
  return resolvePhoneShare(shares, normalizePhone(input.viewerPhone || ''));
};

export const replaceSessionViewerShareOptimistic = (input: {
  shares: Record<string, any> | undefined | null;
  fraction: number;
  isOrganizer: boolean;
  viewerPhone: string;
  organizerPhone?: string;
  name?: string;
  forceEqualJoin?: boolean;
  lockedSharePhones?: Iterable<string>;
  coveredForPhone?: string;
  coveredForName?: string;
}) => {
  const original = normalizeShareMap(input.shares || {});
  const next = { ...original };
  const normalizedViewer = normalizePhone(input.viewerPhone || '');
  const normalizedOrganizer = normalizePhone(input.organizerPhone || '');
  const effectiveOrganizerPhone = normalizedOrganizer || normalizedViewer;
  const lockedPhones = toLockedPhoneSet(input.lockedSharePhones);
  const existingViewerShare = resolveSessionViewerShare({
    shares: original,
    isOrganizer: input.isOrganizer,
    viewerPhone: input.viewerPhone,
    organizerPhone: input.organizerPhone,
  });
  const editableState = computeViewerEditableShareState({
    shares: original,
    isOrganizer: input.isOrganizer,
    viewerPhone: input.viewerPhone,
    organizerPhone: input.organizerPhone,
    lockedSharePhones: input.lockedSharePhones,
  });
  const hadViewerShare = Number(existingViewerShare?.fraction || 0) > EPSILON;
  const currentTotal = sumShareFractions(original);
  const otherPositiveShares = Object.entries(original).filter(([key, share]) => {
    const keyNorm = normalizePhone(key);
    const isViewerKey = input.isOrganizer
      ? (key === 'organizer' || (effectiveOrganizerPhone && keyNorm === effectiveOrganizerPhone))
      : (normalizedViewer && keyNorm === normalizedViewer);
    return !isViewerKey && Number((share as any)?.fraction || 0) > EPSILON;
  });
  const existingViewerKey = input.isOrganizer
    ? (original.organizer ? 'organizer' : (resolvePhoneKey(original, effectiveOrganizerPhone) || null))
    : (resolvePhoneKey(original, normalizedViewer) || null);
  const viewerKey = existingViewerKey || (input.isOrganizer ? (normalizedOrganizer ? 'organizer' : normalizedViewer) : normalizedViewer);

  Object.keys(next).forEach((key) => {
    const keyNorm = normalizePhone(key);
    if (input.isOrganizer) {
      if (key === 'organizer' || (effectiveOrganizerPhone && keyNorm === effectiveOrganizerPhone)) {
        delete next[key];
      }
    } else if (normalizedViewer && keyNorm === normalizedViewer) {
      delete next[key];
    }
  });

  const targetKey = viewerKey;
  if (targetKey) {
    next[targetKey] = {
      fraction: Math.max(0, Number(input.fraction || 0)),
      ...(input.name ? { name: input.name } : {}),
      ...(input.coveredForPhone ? { coveredForPhone: input.coveredForPhone } : {}),
      ...(input.coveredForName ? { coveredForName: input.coveredForName } : {}),
      mode: (!hadViewerShare && editableState.equalizableOtherCount === 0 && Number(input.fraction || 0) >= editableState.editableMaxFraction - EPSILON)
        ? 'equal'
        : 'custom',
    };
  }

  const shouldEqualJoin = Boolean(input.forceEqualJoin) || (!hadViewerShare
    && currentTotal >= 1 - EPSILON
    && Number(input.fraction || 0) >= 1 - EPSILON
    && otherPositiveShares.length > 0);

  if (shouldEqualJoin) {
    const equalized = { ...next };
    const fixedTotal = Object.entries(equalized)
      .filter(([key, share]) => isFixedShareForViewer(key, share, viewerKey, lockedPhones, effectiveOrganizerPhone))
      .reduce((sum, [_, share]) => sum + Number((share as any)?.fraction || 0), 0);
    const equalizableKeys = Array.from(new Set([
      viewerKey,
      ...Object.entries(equalized)
        .filter(([key, share]) => (
          key !== viewerKey
          && !isFixedShareForViewer(key, share, viewerKey, lockedPhones, effectiveOrganizerPhone)
          && Number((share as any)?.fraction || 0) > EPSILON
        ))
        .map(([key]) => key),
    ]));
    const equalizableTotal = Math.max(0, 1 - fixedTotal);
    let allocated = 0;
    equalizableKeys.forEach((key, index) => {
      const fraction = index === equalizableKeys.length - 1
        ? Math.max(0, equalizableTotal - allocated)
        : Number((equalizableTotal / equalizableKeys.length).toFixed(4));
      allocated += fraction;
      equalized[key] = {
        ...(equalized[key] || {}),
        ...(key === viewerKey && input.name ? { name: input.name } : {}),
        ...(key === viewerKey && input.coveredForPhone ? { coveredForPhone: input.coveredForPhone } : {}),
        ...(key === viewerKey && input.coveredForName ? { coveredForName: input.coveredForName } : {}),
        fraction: Number(fraction.toFixed(4)),
        mode: 'equal',
      };
    });
    Object.keys(equalized).forEach((key) => {
      if (isFixedShareForViewer(key, equalized[key], viewerKey, lockedPhones, effectiveOrganizerPhone)) {
        equalized[key] = {
          ...(equalized[key] || {}),
          mode: 'custom',
        };
        return;
      }
      if (!equalizableKeys.includes(key)) {
        delete equalized[key];
      }
    });
    return rebalanceEqualShares(equalized, {
      viewerKey,
      lockedPhones,
      organizerPhone: effectiveOrganizerPhone,
    });
  }

  return rebalanceEqualShares(next, {
    viewerKey,
    lockedPhones,
    organizerPhone: effectiveOrganizerPhone,
  });
};

export const removeSessionViewerShareOptimistic = (input: {
  shares: Record<string, any> | undefined | null;
  isOrganizer: boolean;
  viewerPhone: string;
  organizerPhone?: string;
  rebalanceEqual?: boolean;
}) => {
  const next = { ...(input.shares || {}) };
  const normalizedViewer = normalizePhone(input.viewerPhone || '');
  const normalizedOrganizer = normalizePhone(input.organizerPhone || '');
  const effectiveOrganizerPhone = normalizedOrganizer || normalizedViewer;

  let viewerShare: any = null;
  Object.keys(next).forEach((key) => {
    const keyNorm = normalizePhone(key);
    if (input.isOrganizer) {
      if (key === 'organizer' || (effectiveOrganizerPhone && keyNorm === effectiveOrganizerPhone)) {
        viewerShare = next[key];
        delete next[key];
      }
    } else if (normalizedViewer && keyNorm === normalizedViewer) {
      viewerShare = next[key];
      delete next[key];
    }
  });

  const wasCustom = viewerShare?.mode === 'custom';
  if (wasCustom || input.rebalanceEqual === false) {
    return next;
  }

  const remainingKeys = Object.keys(next);
  if (remainingKeys.length === 0) return next;
  const anyRemainingCustom = remainingKeys.some((k) => next[k]?.mode === 'custom' || next[k]?.isLocked);
  if (!anyRemainingCustom) {
    const equalFraction = Number((1 / remainingKeys.length).toFixed(4));
    let allocated = 0;
    remainingKeys.forEach((key, idx) => {
      const frac = idx === remainingKeys.length - 1 ? Number((1 - allocated).toFixed(4)) : equalFraction;
      allocated += frac;
      next[key] = {
        ...(next[key] || {}),
        fraction: frac,
        mode: 'equal',
      };
    });
  }

  return next;
};

export const classifyEqualVsCustomSplit = (
  shares: Record<string, any> | undefined | null,
  tolerance = 0.0002,
) => {
  const fractions = Object.values(shares || {})
    .map((share: any) => Number(share?.fraction || 0))
    .filter((fraction) => fraction > EPSILON);
  if (fractions.length <= 1) return 'equal';
  const min = Math.min(...fractions);
  const max = Math.max(...fractions);
  return (max - min) <= tolerance ? 'equal' : 'custom';
};

export const isEqualSplit = (shares: Record<string, any> | undefined | null): boolean => {
  const entries = Object.values(shares || {}).filter((s) => Number(s?.fraction || 0) > EPSILON);
  if (entries.length <= 1) return true;
  const firstFrac = Number(entries[0]?.fraction || 0);
  return entries.every((s) => Math.abs(Number(s?.fraction || 0) - firstFrac) <= EQUAL_SHARE_TOLERANCE);
};

type SessionClaimLogLike = {
  id?: string;
  itemId?: string;
  actorPhone?: string;
  actorName?: string;
  action?: string;
  source?: string;
  requestedFraction?: number;
  rebalanceMode?: 'direct' | 'claim_remaining' | 'equal_join' | 'equalize' | string;
  targetClaimLogId?: string;
  afterShares?: Record<string, any>;
  beforeShares?: Record<string, any>;
};

const normalizeSessionClaimLogs = (logs: any[] | undefined | null): SessionClaimLogLike[] =>
  ((Array.isArray(logs) ? logs : [])
    .map((log: any) => {
      const action = String(log?.action || '').trim();
      if (action !== 'claim' && action !== 'unclaim') return null;
      const id = String(log?.id || '').trim();
      const itemId = String(log?.itemId || '').trim();
      const actorPhone = normalizePhone(String(log?.actorPhone || ''));
      if (!id || !itemId || !actorPhone) return null;
      const requestedFraction = Number(log?.requestedFraction);
      return {
        id,
        itemId,
        actorPhone,
        ...(log?.actorName ? { actorName: String(log.actorName) } : {}),
        action,
        ...(log?.source ? { source: String(log.source) } : {}),
        ...(Number.isFinite(requestedFraction) ? { requestedFraction } : {}),
        ...(log?.rebalanceMode ? { rebalanceMode: String(log.rebalanceMode) } : {}),
        ...(log?.targetClaimLogId ? { targetClaimLogId: String(log.targetClaimLogId) } : {}),
        ...(log?.afterShares ? { afterShares: log.afterShares } : {}),
        ...(log?.beforeShares ? { beforeShares: log.beforeShares } : {}),
      };
    })
    .filter(Boolean)) as SessionClaimLogLike[];

const buildActiveClaimLogIndex = (claimLogs: SessionClaimLogLike[]) => {
  const active = new Set<string>();
  const orderedClaimIds: string[] = [];
  const claimById = new Map<string, SessionClaimLogLike>();
  const baselineUnclaims = new Map<string, Set<string>>();
  const latestActiveClaimIdByActorItem = new Map<string, string>();

  claimLogs.forEach((log) => {
    if (log.source === 'organizer_seed' || log.source === 'organizer_edit') return;
    if (log.action === 'claim') {
      const actorItemKey = `${log.itemId}::${log.actorPhone}`;
      const previousClaimId = latestActiveClaimIdByActorItem.get(actorItemKey);
      if (previousClaimId) active.delete(previousClaimId);
      active.add(String(log.id));
      orderedClaimIds.push(String(log.id));
      claimById.set(String(log.id), log);
      latestActiveClaimIdByActorItem.set(actorItemKey, String(log.id));
      return;
    }
    if (!log.targetClaimLogId) {
      const existing = baselineUnclaims.get(String(log.itemId)) || new Set<string>();
      existing.add(String(log.actorPhone));
      baselineUnclaims.set(String(log.itemId), existing);
      return;
    }
    active.delete(String(log.targetClaimLogId));
    const claimedLog = claimById.get(String(log.targetClaimLogId));
    if (claimedLog) {
      const actorItemKey = `${claimedLog.itemId}::${claimedLog.actorPhone}`;
      if (latestActiveClaimIdByActorItem.get(actorItemKey) === log.targetClaimLogId) {
        latestActiveClaimIdByActorItem.delete(actorItemKey);
      }
    }
  });

  return {
    activeClaimIds: orderedClaimIds.filter((id) => active.has(id)),
    claimById,
    baselineUnclaims,
  };
};

const applyClaimLogToShares = (input: {
  shares: Record<string, any> | undefined | null;
  claimLog: SessionClaimLogLike;
  lockedPhones?: Set<string>;
}) => {
  if ((input.claimLog.source === 'set_shares' || input.claimLog.action === 'set_shares') && input.claimLog.afterShares) {
    return normalizeShareMap(input.claimLog.afterShares);
  }
  const nextShares = normalizeShareMap(input.shares || {});
  const actorPhone = String(input.claimLog.actorPhone || '');
  const requestedFraction = Number(Number(input.claimLog.requestedFraction || 0).toFixed(4));
  const mode = String(input.claimLog.rebalanceMode || 'direct');

  if (!nextShares[actorPhone]) {
    nextShares[actorPhone] = {
      fraction: 0,
      ...(input.claimLog.actorName ? { name: input.claimLog.actorName } : {}),
    };
  } else if (input.claimLog.actorName && !nextShares[actorPhone].name) {
    nextShares[actorPhone].name = input.claimLog.actorName;
  }

  if (mode === 'equal_join' || mode === 'equalize') {
    const fixedTotal = Object.entries(nextShares)
      .filter(([phone, share]) => isFixedShareForViewer(phone, share, actorPhone, input.lockedPhones))
      .reduce((sum, [_, share]) => sum + Number((share as any)?.fraction || 0), 0);
    const claimants = Array.from(new Set([
      actorPhone,
      ...Object.entries(nextShares)
        .filter(([phone, share]) => (
          phone !== actorPhone
          && !isFixedShareForViewer(phone, share, actorPhone, input.lockedPhones)
          && Number((share as any)?.fraction || 0) > EPSILON
        ))
        .map(([phone]) => phone),
    ]));
    const equalizableTotal = Math.max(0, 1 - fixedTotal);
    let allocated = 0;
    claimants.forEach((phone, index) => {
      const fraction = index === claimants.length - 1
        ? Math.max(0, equalizableTotal - allocated)
        : Number((equalizableTotal / claimants.length).toFixed(4));
      allocated += fraction;
      nextShares[phone] = {
        ...(nextShares[phone] || {}),
        fraction: Number(fraction.toFixed(4)),
        mode: 'equal',
      };
    });
    Object.keys(nextShares).forEach((phone) => {
      if (isFixedShareForViewer(phone, nextShares[phone], actorPhone, input.lockedPhones)) {
        nextShares[phone] = {
          ...(nextShares[phone] || {}),
          mode: 'custom',
        };
        return;
      }
      if (!claimants.includes(phone)) delete nextShares[phone];
    });
    return normalizeShareMap(nextShares);
  }

  const fixedOtherSharesSum = Object.entries(nextShares)
    .filter(([phone, share]) => phone !== actorPhone && isFixedShareForViewer(phone, share, actorPhone, input.lockedPhones))
    .reduce((sum, [_, share]) => sum + Number((share as any)?.fraction || 0), 0);
  const maxAvailable = Math.max(0, 1 - fixedOtherSharesSum);
  const equalizableOtherCount = Object.entries(nextShares)
    .filter(([phone, share]) => (
      phone !== actorPhone
      && !isFixedShareForViewer(phone, share, actorPhone, input.lockedPhones)
      && Number((share as any)?.fraction || 0) > EPSILON
    ))
    .length;
  const directMaxAvailable = Math.max(0, maxAvailable - (equalizableOtherCount * MIN_AUTO_SHARE_FRACTION));
  const appliedFraction = mode === 'claim_remaining'
    ? directMaxAvailable
    : Math.min(requestedFraction, directMaxAvailable);

  if (appliedFraction <= EPSILON) {
    delete nextShares[actorPhone];
    return normalizeShareMap(nextShares);
  }

  const equalizableOthersCount = Object.entries(nextShares)
    .filter(([phone, share]) => (
      phone !== actorPhone
      && !isFixedShareForViewer(phone, share, actorPhone, input.lockedPhones)
      && Number((share as any)?.fraction || 0) > EPSILON
    ))
    .length;
  const shouldKeepActorAuto = equalizableOthersCount === 0 && appliedFraction >= maxAvailable - EPSILON;

  nextShares[actorPhone] = {
    ...nextShares[actorPhone],
    fraction: Number(appliedFraction.toFixed(4)),
    mode: shouldKeepActorAuto ? 'equal' : 'custom',
  };

  return rebalanceEqualShares(nextShares, {
    viewerKey: actorPhone,
    lockedPhones: input.lockedPhones,
  });
};

export const materializeSessionItemShares = (input: {
  item: any;
  claimLogs?: any[];
  lockedSharePhones?: Iterable<string>;
}) => {
  const itemId = String(input.item?.id || '');
  const lockedPhones = toLockedPhoneSet(input.lockedSharePhones);
  const claimLogs = normalizeSessionClaimLogs(input.claimLogs || []);
  const { activeClaimIds, claimById, baselineUnclaims } = buildActiveClaimLogIndex(claimLogs);

  let shares = normalizeShareMap(input.item?.shares || input.item?.baseShares || {});
  const removedActors = baselineUnclaims.get(itemId);
  if (removedActors) {
    removedActors.forEach((actorPhone) => {
      // If a newer base share explicitly includes this actor, keep it.
      if (Number(shares[actorPhone]?.fraction || 0) > EPSILON) return;
      delete shares[actorPhone];
    });
  }

  activeClaimIds.forEach((claimId) => {
    const claimLog = claimById.get(claimId);
    if (!claimLog || String(claimLog.itemId || '') !== itemId) return;
    shares = applyClaimLogToShares({
      shares,
      claimLog,
      lockedPhones,
    });
  });

  return normalizeShareMap(shares);
};

export const formatFractionAsShareString = (fraction: number, totalParticipants = 3): string => {
  const clamped = Math.max(0, Math.min(1.0, fraction));
  if (clamped >= 0.9999) return '100%';
  if (clamped <= 0.0001) return '0%';

  const percent = Math.round(clamped * 1000) / 10;
  return percent % 1 === 0 ? `${percent}%` : `${percent.toFixed(1)}%`;
};
